import { type FormEvent, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { IconArrowRight, IconQrcode } from "@tabler/icons-react";
import BottomPanel from "../app/panels/BottomPanel";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { isAndroidApp } from "../../tools/android";
import AppleLogo from "../../assets/images/apple_logo_black.svg";
import GoogleLogo from "../../assets/images/google_logo.svg";
import RoomLogo from "../room/RoomLogo";

type LoginSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  onOAuth: (provider: "apple" | "google") => void;
  onEmailSubmit: (email: string) => void;
  // Fires on the first keystroke in the address field — null on the hosts that
  // do not report sheet dismissal.
  onEmailFirstTyped: (() => void) | null;
  // Room QR-code signup entry (native apps only) — null hides the option.
  onQRCodeScan: (() => void) | null;
  // Logo of the room the user was invited to (QR scan / invite link) — when
  // non-null the sheet shows the room logo and the invited copy, confirming
  // the invite was taken into account.
  invitedRoomLogoUrl: string | null;
  // The terms / privacy / healthcare-professional line. The claim has to be
  // made once before the account exists, and the host that already prints it
  // under its own CTA (`/welcome`) must not print it twice.
  displaysLegalNotice: boolean;
  disabled: boolean;
};

function useKeyboardHeight() {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  useEffect(() => {
    if (!isAndroidApp()) return;

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        setIsKeyboardOpen(true);
      }
    };

    document.addEventListener("focusin", handleFocusIn);

    return () => {
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, []);

  return isKeyboardOpen;
}

export default function LoginSheet({
  isOpen,
  onClose,
  onOAuth,
  onEmailSubmit,
  onEmailFirstTyped,
  onQRCodeScan,
  invitedRoomLogoUrl,
  displaysLegalNotice,
  disabled,
}: LoginSheetProps) {
  const { t } = useTranslation();
  const emailRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState("");
  const isEmailEmpty = email.trim() === "";
  const isKeyboardOpen = useKeyboardHeight();
  const height = isKeyboardOpen ? "85dvh" : undefined;

  useEffect(() => {
    if (!isAndroidApp()) return;

    document.documentElement.classList.add("keyboard-managed");

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target;
      if (
        !(target instanceof HTMLInputElement) &&
        !(target instanceof HTMLTextAreaElement) &&
        !(target instanceof HTMLSelectElement)
      ) {
        return;
      }
      setTimeout(() => {
        target.style.scrollMarginBottom = "300px";
        target.scrollIntoView({ block: "end", behavior: "smooth" });
        setTimeout(() => {
          target.style.scrollMarginBottom = "";
        }, 300);
      }, 100);
    };

    document.addEventListener("focusin", handleFocusIn);

    return () => {
      document.documentElement.classList.remove("keyboard-managed");
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, []);

  const handleEmailSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (trimmed !== "") {
      onEmailSubmit(trimmed);
    }
  };

  return (
    <BottomPanel
      isOpen={isOpen}
      onClose={onClose}
      height={height}
      displayCloseButton
    >
      <div className="flex flex-col gap-6 px-6 pb-6 pt-4">
        {/* Room logo (if invited) */}
        {invitedRoomLogoUrl !== null && (
          <div className="w-37.5 min-h-12 mx-auto p-4 flex items-center justify-center rounded-2xl border border-[rgba(11,17,16,0.18)] bg-white">
            <RoomLogo
              style={{ width: 48, height: 48 }}
              logoUrl={invitedRoomLogoUrl}
            />
          </div>
        )}

        {/* Title */}
        <div className="flex flex-col gap-3">
          <h2 className="font-['DM_Sans',sans-serif] font-semibold text-[30px]/8  tracking-[-1px] text-ds-black">
            {invitedRoomLogoUrl !== null
              ? t("Create a free account")
              : t("Sign up for free!")}
          </h2>
          <p className="font-['DM_Sans',sans-serif] font-normal text-[15px]/6  tracking-[-0.15px] text-[rgba(11,17,16,0.46)]">
            {invitedRoomLogoUrl !== null
              ? t("to access your room, and its exclusive content")
              : t(
                  "Stay current with the latest medical research, tailored to your specialty."
                )}
          </p>
        </div>

        {/* Sign up options */}
        <div className="flex flex-col gap-5 items-center">
          <div className="flex flex-col gap-3 w-full">
            {!isAndroidApp() && (
              <Button
                variant="outline"
                size="lg"
                disabled={disabled}
                onClick={() => {
                  onOAuth("apple");
                }}
              >
                <img className="h-4.5" src={AppleLogo} alt="" />
                {t("Sign up with Apple")}
              </Button>
            )}
            <Button
              variant="outline"
              size="lg"
              disabled={disabled}
              onClick={() => {
                onOAuth("google");
              }}
            >
              <img className="h-4.5" src={GoogleLogo} alt="" />
              {t("Sign up with Google")}
            </Button>
            {/* QR code signup (native apps only) */}
            {onQRCodeScan !== null && (
              <Button
                variant="outline"
                size="lg"
                disabled={disabled}
                onClick={onQRCodeScan}
              >
                <IconQrcode />
                {t("Join room via QR code")}
              </Button>
            )}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-1 w-full">
            <div className="flex-1 h-px bg-[rgba(11,17,16,0.18)]" />
            <span className="font-['DM_Sans',sans-serif] font-medium text-caption/4  tracking-[-0.11px] text-[rgba(11,17,16,0.46)] px-1">
              {t("OR")}
            </span>
            <div className="flex-1 h-px bg-[rgba(11,17,16,0.18)]" />
          </div>

          {/* Email input */}
          <form className="w-full" onSubmit={handleEmailSubmit}>
            <Input
              ref={emailRef}
              type="email"
              placeholder={t("Personal or work email")}
              disabled={disabled}
              value={email}
              onChange={(e) => {
                if (
                  email === "" &&
                  e.target.value !== "" &&
                  onEmailFirstTyped !== null
                ) {
                  onEmailFirstTyped();
                }
                setEmail(e.target.value);
              }}
              trailing={
                <Button
                  type="submit"
                  variant={isEmailEmpty ? undefined : "primary"}
                  size="icon"
                  mode="iconOnly"
                  className="size-9 shrink-0 rounded-lg p-0"
                  disabled={disabled || isEmailEmpty}
                >
                  <IconArrowRight />
                </Button>
              }
            />
          </form>
        </div>

        {/* Terms, privacy, and the healthcare-professional certification. The
            carousel still opens on the HCP gate; this line is what makes the
            claim before the account exists. */}
        {displaysLegalNotice && (
          <p className="font-dm-sans text-ui-caption font-normal text-center text-ds-black/45 px-2">
            <Trans
              i18nKey="By continuing, you accept the <termsLink>Terms of Use</termsLink> and the <policyLink>Privacy Policy</policyLink>, and certify that you are a healthcare professional."
              t={t}
              components={{
                termsLink: (
                  <Link
                    className="font-bold text-ds-mint-800 underline"
                    to="/terms?isPush=true&hideTitle=true"
                  />
                ),
                policyLink: (
                  <Link
                    className="font-bold text-ds-mint-800 underline"
                    to="/policy?isPush=true&hideTitle=true"
                  />
                ),
              }}
            />
          </p>
        )}
      </div>
    </BottomPanel>
  );
}
